import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner, StatusBadge, CategoryIcon } from "@/components/common";
import { TxTable } from "@/pages/seller/SellerMoney";
import { api, naira, fmtDateTime, errMsg } from "@/lib/api";

export function AdminOrders() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/orders").then((r) => setItems(r.data)); }, []);
  if (!items) return <Spinner />;
  return (
    <div className="card-surface divide-y divide-border">
      {items.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-order-${o.id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-medium">{o.items.map((i) => i.title).join(", ")}</p>
            <p className="font-mono-ui text-xs text-muted-foreground">{o.order_ref} · {o.buyer_name} · {o.provider} · {fmtDateTime(o.created_at)}</p>
          </div>
          <span className="font-semibold">{naira(o.total)}</span><StatusBadge s={o.status} />
        </div>
      ))}
    </div>
  );
}

export function AdminTransactions() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/transactions").then((r) => setItems(r.data)); }, []);
  return !items ? <Spinner /> : <TxTable items={items} showUser />;
}

export function AdminCategories() {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { try { await api.post("/admin/categories", { name }); setName(""); toast.success("Category added"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = async (c) => { await api.put(`/admin/categories/${c.id}`, { name: c.name, icon: c.icon, order: c.order, active: !c.active }); load(); };
  const del = async (c) => { const { data } = await api.delete(`/admin/categories/${c.id}`); toast.success(data.deactivated ? "Category in use — deactivated instead" : "Category deleted"); load(); };
  if (!items) return <Spinner />;
  return (
    <div className="max-w-2xl">
      <div className="mb-4 flex gap-2">
        <Input data-testid="new-category-input" placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button onClick={add} disabled={name.length < 2} data-testid="add-category-btn"><Plus size={15} className="mr-1" />Add</Button>
      </div>
      <div className="card-surface divide-y divide-border">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`admin-category-${c.slug}`}>
            <CategoryIcon name={c.icon} size={16} className="text-muted-foreground" />
            <span className="flex-1 font-medium">{c.name}</span>
            <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`toggle-category-${c.slug}`} />
            <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(c)} data-testid={`delete-category-${c.slug}`}><Trash2 size={15} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const FIELDS = [
  ["commission_percent", "Platform commission (%)", "Taken from each sale before crediting the seller"],
  ["buyer_fee_percent", "Buyer service fee (%)", "Added on top of the template price at checkout"],
  ["withdrawal_fee", "Withdrawal fee (₦)", "Flat fee deducted from each payout"],
  ["min_withdrawal", "Minimum withdrawal (₦)", ""],
  ["settlement_days", "Settlement period (days)", "Days before a sale moves from pending to available"],
];

export function AdminSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;
  const save = async () => {
    try {
      const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, Number(s[k])])), auto_approve_templates: s.auto_approve_templates };
      const { data } = await api.put("/admin/settings", body); setS(data); toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const example = 3000;
  return (
    <div className="grid max-w-4xl gap-6 md:grid-cols-[1fr_280px]">
      <div className="card-surface space-y-5 p-6">
        {FIELDS.map(([k, l, h]) => (
          <div key={k} className="space-y-1.5"><Label>{l}</Label><Input data-testid={`setting-${k}`} type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} />{h && <p className="text-xs text-muted-foreground">{h}</p>}</div>
        ))}
        <div className="flex items-center justify-between"><Label>Auto-approve new templates</Label><Switch checked={s.auto_approve_templates} onCheckedChange={(v) => setS({ ...s, auto_approve_templates: v })} data-testid="setting-auto-approve" /></div>
        <Button onClick={save} data-testid="save-settings-btn">Save settings</Button>
      </div>
      <div className="card-surface h-fit space-y-2 p-5 text-sm" data-testid="commission-example">
        <p className="eyebrow mb-2">Example sale</p>
        <div className="flex justify-between"><span className="text-muted-foreground">Template price</span><span>{naira(example)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Platform ({s.commission_percent}%)</span><span>{naira(example * s.commission_percent / 100)}</span></div>
        <div className="flex justify-between font-semibold"><span>Seller receives</span><span>{naira(example - example * s.commission_percent / 100)}</span></div>
      </div>
    </div>
  );
}

export function AdminEmails() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/emails").then((r) => setItems(r.data)); }, []);
  if (!items) return <Spinner />;
  return (
    <div>
      <p className="mb-4 text-xs text-muted-foreground">Email outbox. With EMAIL_PROVIDER=log, emails are recorded here instead of being sent. Set EMAIL_PROVIDER=smtp + SMTP_* vars to deliver.</p>
      <div className="card-surface divide-y divide-border">
        {items.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`email-${m.id}`}>
            <div className="min-w-0 flex-1"><p className="font-medium">{m.subject}</p><p className="truncate text-xs text-muted-foreground">{m.to} · {m.template} · {fmtDateTime(m.created_at)}</p>{m.link && <a href={m.link} className="break-all text-xs text-primary" data-testid={`email-link-${m.id}`}>{m.link}</a>}</div>
            <StatusBadge s={m.status === "logged" ? "pending" : m.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
